import { API, Auth } from 'aws-amplify'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import CountryPicker from '../../components/CountryPicker'
import LanguageDropdown from '../../components/languageDropdown'
import ResultModal from '../../components/ResultModal'
import countries from '../../config/countries'
import { apiGet } from '../../libs/apiHelper'
import { useAppContext } from '../../libs/context'
import ProviderConfiguration from './components/ProviderConfiguration.jsx'

const Profile = () => {
  const { userInfo: contextUserInfo } = useAppContext()
  const isAdmin = contextUserInfo?.isAdmin || false
  const [countryCode, setCountryCode] = useState('af')
  const [countryPrefix, setCountryPrefix] = useState('93')
  const [userInfo, setUserInfo] = useState(null)
  const [loading, setLoading] = useState(false)
  const [openResult, setOpenResult] = useState(false)
  const { t, i18n } = useTranslation()

  const modalMsg = {
    ok: {
      title: t('operation_performed'),
      content: t('profile_changed'),
      cta: 'Ok'
    },
    error: {
      title: 'Error',
      content: t('error_operation'),
      cta: 'Error'
    }
  }
  const [modalContent, setModalContent] = useState(modalMsg.ok)

  const getInfo = async () => {
    // Use apiGet helper which handles impersonation automatically
    const userFullInfo = await apiGet('/user')
    const prefix = userFullInfo.countryCode ? userFullInfo.countryCode : '93'
    const countrie = countries.find((c) => c.code == prefix)
    setCountryCode(countrie.iso)
    setCountryPrefix(countrie.code)
    setUserInfo(userFullInfo)
  }

  const updateUserCountryCode = async (countryCode) => {
    if (userInfo && userInfo.countryCode != countryCode) {
      let changedClient = {
        [userInfo.userId]: { countryCode: countryCode }
      }
      setLoading(true)
      setOpenResult(true)
      try {
        const tokenReq = (await Auth.currentAuthenticatedUser())
          .signInUserSession.idToken.jwtToken
        await API.post('rcs-api', '/users', {
          headers: {
            Authorization: tokenReq
          },
          body: changedClient
        })
        setUserInfo((oldUser) => {
          oldUser.countryCode = countryCode
          return oldUser
        })
      } catch (err) {
        console.log(err)
      }
      setLoading(false)
    }
  }

  useEffect(() => {
    getInfo()
  }, [])

  // Get user name from email or userId
  const getUserName = () => {
    if (!userInfo) return ''
    if (userInfo.name) return userInfo.name
    if (userInfo.email) return userInfo.email.split('@')[0]
    return userInfo.userId?.split('-')[0] || ''
  }

  // Check if we're impersonating
  const isImpersonating =
    contextUserInfo?.impersonatedUserId && contextUserInfo?.isAdmin

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100/50">
      <ResultModal
        {...modalContent}
        isLoading={loading}
        open={openResult}
        setOpen={setOpenResult}
      />

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Impersonation Banner */}
        {isImpersonating && (
          <div className="mb-6 bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-md">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-yellow-400"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-yellow-700">
                  <span className="font-medium">Viendo perfil como:</span>{' '}
                  {contextUserInfo.impersonatedEmail ||
                    contextUserInfo.impersonatedUsername ||
                    contextUserInfo.impersonatedUserId}
                </p>
                <p className="text-xs text-yellow-600 mt-1">
                  Estás viendo el perfil del usuario impersonado. Los cambios se
                  aplicarán a este usuario.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Header with greeting */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            {t('hello')},{' '}
            <span className="text-indigo-600">{getUserName()}</span>
          </h1>
        </div>

        {/* General Settings Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
              <svg
                className="w-5 h-5 text-gray-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {t('general_settings')}
              </h2>
              <p className="text-sm text-gray-500">
                {t('general_settings_desc')}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Language */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {i18n.t('language')}
              </label>
              <LanguageDropdown />
            </div>

            {/* Country */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('country_prefix')}
              </label>
              <div className="max-w-xs">
                <CountryPicker
                  countryCode={countryCode}
                  setCountryCode={setCountryCode}
                  setCountryPrefix={setCountryPrefix}
                  countryPrefix={countryPrefix}
                  updateUserCountryCode={updateUserCountryCode}
                />
              </div>
            </div>
          </div>
        </div>

        {/* RCS Provider Configuration Card - Solo visible para admins */}
        {isAdmin && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-indigo-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                  />
                </svg>
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {t('rcs_provider_config')}
                </h2>
                <p className="text-sm text-gray-500">
                  {t('rcs_provider_config_desc')}
                </p>
              </div>
            </div>

            {userInfo && (
              <ProviderConfiguration
                userInfo={userInfo}
                refreshUserInfo={getInfo}
                setLoading={setLoading}
                setOpenResult={setOpenResult}
              />
            )}
          </div>
        )}

        {/* Footer with privacy notice and user ID */}
        <div className="mt-6 text-center space-y-2">
          <p className="text-xs text-gray-500">{t('profile_privacy')}</p>
          {userInfo && (
            <p className="text-xs text-gray-400">User ID: {userInfo.userId}</p>
          )}
        </div>
      </div>
    </div>
  )
}

export default Profile
